/**
 * Spec Phase 4 Slice 4C — SpecBody.
 *
 * 3-column layout shell per brief §4:
 *   left rail (244px) | center paper editor (flex-1) | right dock rail (336px)
 *
 * BUG-040 — when leftCollapsed, render a 40px strip with section
 * numbers instead of fully hiding the rail so the drafter can
 * still navigate while the centre pane uses the freed width.
 * The expand chevron lives on the strip; the collapse chevron
 * lives on SectionNav itself.
 */
import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';

interface Props {
  left?: React.ReactNode;
  center?: React.ReactNode;
  right?: React.ReactNode;
}

export default function SpecBody({ left, center, right }: Props): JSX.Element {
  const leftCollapsed = useSpecStore((s) => s.leftCollapsed);
  const rightCollapsed = useSpecStore((s) => s.rightCollapsed);

  return (
    <div
      className="flex min-h-0 flex-1 overflow-hidden"
      data-testid="spec-body"
    >
      {!leftCollapsed && (
        <aside
          className="flex w-[244px] flex-shrink-0 flex-col border-r border-zinc-200 bg-white"
          data-testid="spec-left-rail"
          aria-label="Section navigation"
        >
          {left ?? <LeftRailPlaceholder />}
        </aside>
      )}
      {leftCollapsed && <CollapsedLeftStrip />}

      <main
        className="flex min-w-0 flex-1 flex-col"
        data-testid="spec-center"
        aria-label="Paper editor"
      >
        {center ?? <CenterPlaceholder />}
      </main>

      {!rightCollapsed && (
        <aside
          className="flex w-[336px] flex-shrink-0 flex-col border-l border-zinc-200 bg-white"
          data-testid="spec-right-rail"
          aria-label="Reference docks"
        >
          {right ?? <RightRailPlaceholder />}
        </aside>
      )}
    </div>
  );
}

/** BUG-040 — collapsed left rail strip. ~40px wide, lists section
 * sequence numbers as clickable buttons; hovering shows the
 * section title via tooltip. The expand chevron sits at the top
 * of the strip; clicking it flips ``leftCollapsed`` back to false
 * and the full SectionNav re-renders. */
function CollapsedLeftStrip(): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const activeSectionId = useSpecStore((s) => s.activeSectionId);
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const toggleLeftRail = useSpecStore((s) => s.toggleLeftRail);
  const visible = sections.filter((s) => !s.is_excluded);
  return (
    <aside
      className="flex w-[40px] flex-shrink-0 flex-col items-center border-r border-zinc-200 bg-white py-2"
      data-testid="spec-left-rail-collapsed"
      aria-label="Section navigation (collapsed)"
    >
      <button
        type="button"
        className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-100"
        onClick={toggleLeftRail}
        aria-label="Expand section panel"
        title="Expand section panel"
        data-testid="spec-left-rail-expand"
      >
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
      </button>
      <ol className="flex w-full flex-1 flex-col items-center gap-0.5 overflow-y-auto">
        {visible.map((s) => {
          const isActive = s.id === activeSectionId;
          const seq = String(s.sequence_num).padStart(2, '0');
          return (
            <li key={s.id} className="w-full">
              <button
                type="button"
                onClick={() => {
                  setActiveSection(s.id);
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent(
                      'spec.scroll-to-section',
                      { detail: { id: s.id } },
                    ));
                  }
                }}
                className={[
                  'mx-auto block w-7 rounded-sm py-1 text-center font-mono text-[10px]',
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-zinc-500 hover:bg-zinc-100',
                  s.is_excluded ? 'opacity-50 line-through' : '',
                ].join(' ')}
                title={`${s.title}${s.is_auto_generated ? ' (auto)' : ''}`}
                aria-label={`Jump to section ${seq}: ${s.title}`}
                aria-current={isActive ? 'true' : undefined}
                data-testid={`spec-left-rail-strip-${s.section_kind}`}
              >
                {seq}
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function LeftRailPlaceholder(): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center text-xs text-zinc-400"
      data-testid="spec-left-placeholder"
    >
      Section navigation lands in Slice 4D.
    </div>
  );
}

function CenterPlaceholder(): JSX.Element {
  return (
    <div
      className="s6-paper-backdrop flex flex-1 items-start justify-center overflow-y-auto px-6 py-12"
      data-testid="spec-center-placeholder"
    >
      <div className="s6-paper">
        <div className="s6-paper-eyebrow">Specification preview</div>
        <h2 className="s6-paper-heading mt-1">
          Paper editor lands in Slice 4D
        </h2>
        <p className="mt-6 text-sm text-zinc-600">
          The Stage 6 workbench is unlocked: drawings are frozen and
          the spec document has been seeded. Sections, paragraphs, and
          numeral chips render here once Slice 4D wires the paper
          surface.
        </p>
      </div>
    </div>
  );
}

function RightRailPlaceholder(): JSX.Element {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center text-xs text-zinc-400"
      data-testid="spec-right-placeholder"
    >
      Reference docks land in Slice 4E.
    </div>
  );
}
